import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MapPin, ArrowRight } from 'lucide-react';
import { worldcupAPI } from '../services/api';
import SEOHead from '../components/SEOHead';

function teamInitials(name) {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const Matches = () => {
  const navigate = useNavigate();
  const { data: matches = [], isLoading } = useQuery({
    queryKey: ['worldcupFixtures'],
    queryFn: () => worldcupAPI.getFixtures().then((r) => r.data),
  });

  if (isLoading) {
    return (
      <>
        <SEOHead
          title="2026 FIFA World Cup Matches & Fixtures - WorldCup Fan Planner"
          description="Browse all 2026 FIFA World Cup fixtures by group. Dates, stadiums, and match-ups for USA, Canada, and Mexico."
        />
        <div className="min-h-[40vh] flex items-center justify-center text-on-surface-variant dark:text-slate-400 font-body">
          Loading fixtures…
        </div>
      </>
    );
  }

  const byGroup = matches.reduce((acc, m) => {
    const g = m.group_stage || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(m);
    return acc;
  }, {});
  const groupOrder = ['Group A', 'Group B', 'Group C', 'Group D', 'Group E', 'Group F', 'Group G', 'Group H'];
  const sortedGroups = [
    ...new Set([...groupOrder.filter((g) => byGroup[g]), ...Object.keys(byGroup).filter((g) => !groupOrder.includes(g))]),
  ];

  return (
    <>
      <SEOHead
        title="2026 FIFA World Cup Matches & Fixtures - WorldCup Fan Planner"
        description="Browse all 2026 FIFA World Cup fixtures by group. Dates, stadiums, and match-ups for USA, Canada, and Mexico."
      />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-12 font-body">
        <header className="mb-10">
          <p className="text-tertiary font-bold text-xs uppercase tracking-widest mb-2">Fixtures</p>
          <h1 className="text-3xl sm:text-4xl font-headline font-bold text-on-surface dark:text-white mb-3">2026 FIFA World Cup matches</h1>
          <p className="text-on-surface-variant dark:text-slate-400 max-w-3xl leading-relaxed">
            Upcoming and past 2026 World Cup fixtures by group. Open a match for win probability, venue context, and head-to-head where available.
          </p>
          <p className="text-sm text-on-surface-variant dark:text-slate-500 mt-4">
            Planning travel? Browse{' '}
            <Link to="/cities" className="text-primary font-semibold hover:underline">
              host cities
            </Link>
            ,{' '}
            <Link to="/stadiums" className="text-primary font-semibold hover:underline">
              stadium guides
            </Link>
            , and the{' '}
            <Link to="/travel-planner" className="text-primary font-semibold hover:underline">
              travel planner
            </Link>
            .
          </p>
        </header>

        <div className="space-y-12">
          {sortedGroups.map((group) => (
            <section key={group} aria-labelledby={`group-${group.replace(/\s+/g, '-')}`}>
              <div className="flex items-center justify-between gap-4 mb-6">
                <h2 id={`group-${group.replace(/\s+/g, '-')}`} className="text-xl font-headline font-bold text-on-surface dark:text-white">
                  {group}
                </h2>
                <Link to="/tickets" className="hidden sm:inline-flex items-center gap-1 text-sm font-bold text-primary hover:underline">
                  Find tickets <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </div>
              <div className="grid gap-5">
                {byGroup[group].map((m) => (
                  <article
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/matches/${m.match_number}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/matches/${m.match_number}`);
                      }
                    }}
                    className="bg-surface-container-lowest dark:bg-slate-800/70 rounded-2xl p-6 sm:p-8 cursor-pointer border border-outline-variant/10 dark:border-slate-700 shadow-sm hover:shadow-fanhub hover:-translate-y-0.5 transition-all relative overflow-hidden group"
                  >
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-secondary to-tertiary opacity-80" aria-hidden />
                    <div className="flex flex-wrap justify-between gap-2 text-sm text-on-surface-variant dark:text-slate-400 mb-4">
                      <span className="font-semibold text-on-surface dark:text-slate-200">
                        Match {m.match_number != null ? `#${m.match_number}` : ''}
                      </span>
                      <time dateTime={m.date || undefined}>{m.date ? new Date(m.date).toLocaleString() : 'TBD'}</time>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-surface-container-low dark:bg-slate-700 flex items-center justify-center font-black text-lg text-on-surface dark:text-white">
                          {teamInitials(m.team1_name)}
                        </div>
                        <span className="font-headline font-bold text-lg text-on-surface dark:text-white">{m.team1_name || 'TBD'}</span>
                      </div>
                      <div className="text-3xl font-black text-primary tabular-nums">
                        {m.score_team1 != null ? `${m.score_team1} - ${m.score_team2}` : 'vs'}
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-headline font-bold text-lg text-on-surface dark:text-white">{m.team2_name || 'TBD'}</span>
                        <div className="w-14 h-14 rounded-full bg-surface-container-low dark:bg-slate-700 flex items-center justify-center font-black text-lg text-on-surface dark:text-white">
                          {teamInitials(m.team2_name)}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-on-surface-variant dark:text-slate-400 mt-5 flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4 shrink-0 text-secondary" aria-hidden />
                      {m.stadium_slug ? (
                        <Link
                          to={`/stadiums/${m.stadium_slug}`}
                          onClick={(e) => e.stopPropagation()}
                          className="hover:text-primary dark:hover:text-primary-container font-medium"
                        >
                          {m.stadium_name}
                        </Link>
                      ) : (
                        <span>{m.stadium_name}</span>
                      )}
                      {m.stadium_capacity != null && (
                        <span className="text-outline dark:text-slate-500">· {Number(m.stadium_capacity).toLocaleString()} cap.</span>
                      )}
                    </p>
                    <div className="mt-5 flex flex-wrap justify-center gap-3">
                      <span className="inline-flex items-center px-4 py-2 rounded-full bg-secondary-container text-on-secondary-container text-sm font-bold">
                        View analysis
                      </span>
                      <Link
                        to="/tickets"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center px-4 py-2 rounded-full border border-primary/30 text-primary text-sm font-bold hover:bg-primary/10"
                      >
                        Find Tickets
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>
    </>
  );
};

export default Matches;
